import { Navigate, createBrowserRouter, useLocation } from 'react-router-dom';
import type { ReactElement } from 'react';
import { hasPermission } from '@hq/permission';
import { Alert, Card, Spin } from 'antd';
import { lazy, Suspense } from 'react';
import AppLayout from '../layouts/AppLayout';
import ApplyCreate from '../pages/ApplyCreate';
import ApplyDetail from '../pages/ApplyDetail';
import ApplyList from '../pages/ApplyList';
import ApprovalList from '../pages/ApprovalList';
import BasicDept from '../pages/BasicDept';
import BasicSupplier from '../pages/BasicSupplier';
import BasicUser from '../pages/BasicUser';
import BasicWarehouse from '../pages/BasicWarehouse';
import BudgetPlan from '../pages/BudgetPlan';
import BudgetUsage from '../pages/BudgetUsage';
import DeliveryDetail from '../pages/DeliveryDetail';
import DeliveryList from '../pages/DeliveryList';
import DeliveryTrack from '../pages/DeliveryTrack';
import FavoriteList from '../pages/FavoriteList';
import InboundList from '../pages/InboundList';
import Login from '../pages/Login';
import OutboundList from '../pages/OutboundList';
import PurchaseList from '../pages/PurchaseList';
import RequireList from '../pages/RequireList';
import ReturnCreate from '../pages/ReturnCreate';
import ReturnDetail from '../pages/ReturnDetail';
import ReturnList from '../pages/ReturnList';
import SettleInvoice from '../pages/SettleInvoice';
import SettleList from '../pages/SettleList';
import ShortagePage from '../pages/ShortagePage';
import SubstockConsume from '../pages/SubstockConsume';
import SubstockList from '../pages/SubstockList';
import CostTransferList from '../pages/CostTransferList';
import WorkorderList from '../pages/WorkorderList';
import PdaPage from '../pages/PdaPage';
import MaterialList from '../pages/MaterialList';

import NotFound from '../pages/NotFound';
import ScreenDevices from '../pages/ScreenDevices';
import ScreenScan from '../pages/ScreenScan';
import SignList from '../pages/SignList';
import SignScan from '../pages/SignScan';
import StockAlertList from '../pages/StockAlertList';
import StockLedger from '../pages/StockLedger';
import StockList from '../pages/StockList';
import StocktakeList from '../pages/StocktakeList';
import SystemAudit from '../pages/SystemAudit';
import SystemBudgetQuota from '../pages/SystemBudgetQuota';
import SystemDict from '../pages/SystemDict';
import SystemFlow from '../pages/SystemFlow';
import TransferList from '../pages/TransferList';
import Workbench from '../pages/Workbench';
import OpeningPage from '../pages/OpeningPage';
import LabelPrintPage from '../pages/LabelPrintPage';
import { modules } from '../modules/registry';
import { useAuthStore } from '../stores/auth';

/** 已显式实现路由的路径前缀：registry 中的同名页面避免重复注册。 */
const IMPLEMENTED_PREFIXES = [
  'workbench', 'apply', 'shortage', 'approval', 'delivery', 'sign', 'return',
  'stock', 'inventory', 'transfer', 'stocktake', 'settle', 'budget',
  'master', 'dashboard', 'reports', 'require', 'purchase', 'substock', 'cost',
  'workorder', 'pda', 'basic', 'system'
];

/** registry 中尚未接入后端、但已有页面模板的模块：统一挂到路由，避免成为死代码。 */
const REGISTRY_ROUTES = modules
  .flatMap((group) => group.pages)
  .filter((page) => {
    if (page.real || page.path === '/') return false;
    const key = page.path.replace(/^\//, '');
    if (key.length === 0) return false;
    const first = key.split('/')[0] ?? '';
    return !IMPLEMENTED_PREFIXES.includes(first);
  });

// 图表页（含 ECharts）懒加载，首屏不下载 charts 分块（docs/05 §6-12）
const Dashboard = lazy(() => import('../pages/Dashboard'));
const ReportPage = lazy(() => import('../pages/ReportPage'));
// 医院大屏（/screen）：图表重、单独懒加载，登录端首屏不携带大屏代码
const ScreenPage = lazy(() => import('../pages/ScreenPage'));
// registry 模板页（含示例数据生成器 mocks/fixtures）懒加载：当前 REGISTRY_ROUTES 为空，
// 首屏与主包里不应再打包 demo 假数据
const ModuleRoute = lazy(() => import('../pages/ModuleRoute'));

function LazyPage({ children }: { children: ReactElement }) {
  return <Suspense fallback={<Spin style={{ display: 'block', margin: '80px auto' }} />}>{children}</Suspense>;
}

/** 路由守卫：无令牌跳登录；最终权限仍以后端为准（docs/05 §5）。 */
function RequireAuth({ children }: { children: ReactElement }) {
  const token = useAuthStore((state) => state.token);
  const location = useLocation();
  if (!token) return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  return children;
}

/**
 * 路由级权限门禁（纵深防御）：侧栏菜单裁剪只是"看不见"，直接敲 URL 仍能进入页面，
 * 用户会看到一个处处 403 的残页。后端始终校验权限，这里只把失败提前成一句明确提示。
 *
 * 只登记"整页功能唯一对应一组权限码"的路由（权限码取自各页自身的 canManage 判定）；
 * 列表/详情类业务页不登记 —— 它们按权限裁剪按钮，只读账号本来就应该能进。
 */
const ROUTE_PERMS: Record<string, string[]> = {
  'basic/user': ['sys:user:manage', 'sys:role:manage'],
  'basic/dept': ['sys:org:manage'],
  'basic/warehouse': ['sys:org:manage'],
  'inventory/opening': ['wl:inventory:manage'],
  'inventory/label': ['wl:inventory:manage'],
  'system/flow': ['sys:approval:manage'],
  'system/dict': ['sys:config:manage'],
  'system/audit': ['sys:audit:view', 'sys:platform:audit']
};

function Forbidden({ path }: { path: string }) {
  return (
    <Card className="page-card" title="无权限">
      <Alert
        type="warning"
        showIcon
        message="无权访问该页面"
        description={`当前账号缺少 ${path} 所需权限（${(ROUTE_PERMS[path] ?? []).join(' 或 ')}），请联系系统管理员。`}
      />
    </Card>
  );
}

function RequirePerm({ path, children }: { path: string; children: ReactElement }) {
  const profile = useAuthStore((state) => state.profile);
  const required = ROUTE_PERMS[path];
  if (!required?.length) return children;
  if (profile?.level === 'admin') return children;

  const allowed = profile?.perms ? required.some((perm) => hasPermission(profile.perms, perm)) : false;
  return allowed ? children : <Forbidden path={path} />;
}

export const router = createBrowserRouter([
  { path: '/login', element: <Login /> },
  {
    // 大屏全屏路由：不套 AppLayout、不走账号登录态门禁（屏端凭「设备凭据」独立换只读令牌），
    // 未授权时页内展示激活页（屏端凭设备凭据独立取数，与账号登录态无关）
    path: '/screen',
    element: (
      <LazyPage>
        <ScreenPage />
      </LazyPage>
    )
  },
  {
    // PDA 精简视图：无侧栏，供手持浏览器全屏作业（docs/05 §4.1）
    path: '/pda',
    element: (
      <RequireAuth>
        <PdaPage />
      </RequireAuth>
    )
  },
  {
    path: '/',
    element: (
      <RequireAuth>
        <AppLayout />
      </RequireAuth>
    ),
    children: [
      { index: true, element: <Navigate to="/workbench" replace /> },
      { path: 'workbench', element: <Workbench /> },
      { path: 'apply', element: <ApplyList /> },
      { path: 'apply/create', element: <ApplyCreate /> },
      { path: 'apply/favorite', element: <FavoriteList /> },
      { path: 'apply/:id', element: <ApplyDetail /> },
      { path: 'shortage', element: <ShortagePage /> },
      { path: 'approval/todo', element: <ApprovalList mode="todo" /> },
      { path: 'approval/done', element: <ApprovalList mode="done" /> },
      { path: 'delivery', element: <DeliveryList /> },
      { path: 'delivery/track', element: <DeliveryTrack /> },
      { path: 'delivery/:id', element: <DeliveryDetail /> },
      { path: 'sign', element: <SignList /> },
      { path: 'sign/scan', element: <SignScan /> },
      { path: 'screen-devices', element: <ScreenDevices /> },
      { path: 'screen-scan', element: <ScreenScan /> },
      { path: 'inventory/alert', element: <StockAlertList /> },
      { path: 'return', element: <ReturnList /> },
      { path: 'return/create', element: <ReturnCreate /> },
      { path: 'return/:id', element: <ReturnDetail /> },
      { path: 'stock', element: <StockList /> },
      { path: 'stock/ledger', element: <StockLedger /> },
      { path: 'master/material', element: <MaterialList /> },
      { path: 'require', element: <RequireList /> },
      { path: 'purchase', element: <PurchaseList /> },
      { path: 'substock', element: <SubstockList /> },
      { path: 'substock/consume', element: <SubstockConsume /> },
      { path: 'cost', element: <CostTransferList /> },
      { path: 'workorder', element: <WorkorderList /> },
      { path: 'inventory/outbound', element: <OutboundList /> },
      { path: 'inventory/inbound', element: <InboundList /> },
      { path: 'inventory/opening', element: <RequirePerm path="inventory/opening"><OpeningPage /></RequirePerm> },
      { path: 'inventory/label', element: <RequirePerm path="inventory/label"><LabelPrintPage /></RequirePerm> },
      { path: 'transfer', element: <TransferList /> },
      { path: 'stocktake', element: <StocktakeList /> },
      { path: 'settle', element: <SettleList /> },
      { path: 'settle/invoice', element: <SettleInvoice /> },
      { path: 'budget', element: <BudgetUsage /> },
      { path: 'budget/plan', element: <BudgetPlan /> },
      { path: 'basic/dept', element: <RequirePerm path="basic/dept"><BasicDept /></RequirePerm> },
      { path: 'basic/warehouse', element: <RequirePerm path="basic/warehouse"><BasicWarehouse /></RequirePerm> },
      { path: 'basic/supplier', element: <BasicSupplier /> },
      { path: 'basic/user', element: <RequirePerm path="basic/user"><BasicUser /></RequirePerm> },
      { path: 'system/flow', element: <RequirePerm path="system/flow"><SystemFlow /></RequirePerm> },
      { path: 'system/budget', element: <SystemBudgetQuota /> },
      { path: 'system/dict', element: <RequirePerm path="system/dict"><SystemDict /></RequirePerm> },
      { path: 'system/audit', element: <RequirePerm path="system/audit"><SystemAudit /></RequirePerm> },
      {
        path: 'dashboard',
        element: (
          <LazyPage>
            <Dashboard />
          </LazyPage>
        )
      },
      {
        path: 'reports',
        element: (
          <LazyPage>
            <ReportPage />
          </LazyPage>
        )
      },
      // registry 驱动的模板页（列表 / 详情 / 占位），后端未实现的模块在此可达
      ...REGISTRY_ROUTES.map((page) => ({
        path: page.path.replace(/^\//, ''),
        element: (
          <LazyPage>
            <ModuleRoute page={page} />
          </LazyPage>
        )
      })),
      ...REGISTRY_ROUTES.filter((page) => page.detail).map((page) => ({
        path: `${page.path.replace(/^\//, '')}/:id`,
        element: (
          <LazyPage>
            <ModuleRoute page={page} />
          </LazyPage>
        )
      }))
    ]
  },
  { path: '*', element: <NotFound /> }
]);
